import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Banknote, Loader2, Upload, AlertTriangle, FileText } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'

export default function PagamentosLotePage() {
  const qc = useQueryClient()
  const [bancoId, setBancoId] = useState<number | ''>('')
  const [sel, setSel] = useState<Set<number>>(new Set())

  const { data: bancos = [] } = useQuery<any[]>({
    queryKey: ['contas-bancarias'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })
  const { data: pendentes = [], isLoading } = useQuery<any[]>({
    queryKey: ['pagamentos-pendentes'],
    queryFn: async () => (await api.get('/financeiro/pagamentos/pendentes')).data,
  })

  const toggle = (id: number) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const pagaveis = pendentes.filter((p: any) => p.forma_sugerida !== 'sem')
  const selecionarPagaveis = () => setSel(new Set(pagaveis.map((p: any) => p.conta_id)))
  const CHIP: Record<string, string> = { boleto: 'bg-indigo-100 text-indigo-700', pix: 'bg-emerald-100 text-emerald-700', ted: 'bg-blue-100 text-blue-700', sem: 'bg-slate-200 text-slate-500' }

  const gerarRemessa = async () => {
    if (!bancoId) { toast.error('Escolha a conta pagadora'); return }
    if (!sel.size) { toast.error('Selecione contas a pagar'); return }
    const token = localStorage.getItem('pain_token')
    const r = await fetch('/api/financeiro/pagamentos/remessa', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ conta_ids: Array.from(sel), conta_bancaria_id: bancoId }),
    })
    if (!r.ok) { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Erro na remessa'); return }
    const blob = await r.blob()
    const url = URL.createObjectURL(blob); const a = document.createElement('a')
    a.href = url; a.download = `pagamento_${bancoId}_${new Date().toISOString().slice(0, 10)}.REM`; a.click()
    setTimeout(() => URL.revokeObjectURL(url), 60000)
    toast.success('Remessa de pagamento gerada — suba no internet banking')
    setSel(new Set()); qc.invalidateQueries({ queryKey: ['pagamentos-pendentes'] })
  }
  const importarRetorno = async (file: File) => {
    const fd = new FormData(); fd.append('arquivo', file)
    try {
      const d = (await api.post('/financeiro/pagamentos/retorno', fd)).data
      toast.success(`Retorno: ${d.baixados} baixado(s) de ${d.pagos} pago(s)`)
      if (d.nao_encontrados?.length) toast.error(`${d.nao_encontrados.length} não encontrado(s)`)
      qc.invalidateQueries({ queryKey: ['pagamentos-pendentes'] })
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Erro ao importar retorno') }
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold flex items-center gap-2"><Banknote size={18} /> Pagamentos em Lote (CNAB)</h2>

      <div className="card card-body bg-amber-50 border-amber-200 text-xs text-amber-800 flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>Caminho de arquivo (sem API): gera a <strong>remessa de pagamento</strong> dos boletos de fornecedor pra subir no internet banking,
          e importa o <strong>retorno</strong> pra dar baixa + conciliar. Só contas a pagar <strong>com código de barras</strong>. Layout a homologar com o banco.</span>
      </div>

      <div className="card card-body flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Conta pagadora</label>
          <select className="input w-56" value={bancoId} onChange={e => setBancoId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Selecione</option>
            {bancos.map((b: any) => <option key={b.id} value={b.id}>{b.nome}</option>)}
          </select>
        </div>
        <div className="flex-1" />
        <button className="btn-secondary" onClick={selecionarPagaveis} disabled={pagaveis.length === 0}>
          Selecionar pagáveis ({pagaveis.length})
        </button>
        <button className="btn-primary" disabled={!bancoId || sel.size === 0} onClick={gerarRemessa}>
          <FileText size={14} /> Gerar remessa {sel.size ? `(${sel.size})` : ''}
        </button>
        <label className="btn-secondary cursor-pointer">
          <Upload size={14} /> Importar retorno
          <input type="file" className="hidden" accept="*"
            onChange={e => { const f = e.target.files?.[0]; if (f) importarRetorno(f); e.currentTarget.value = '' }} />
        </label>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="w-8"></th><th>Fornecedor</th><th>Descrição</th>
                <th className="text-right">Valor</th><th>Vencimento</th><th>Doc</th><th>Forma</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="text-center py-6 text-ink-400"><Loader2 className="animate-spin inline" size={16} /></td></tr>}
              {!isLoading && pendentes.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-ink-400 text-sm">Nenhuma conta a pagar em aberto.</td></tr>
              )}
              {pendentes.map((p: any) => (
                <tr key={p.conta_id} className={sel.has(p.conta_id) ? 'bg-brand-50' : ''}>
                  <td>{p.forma_sugerida !== 'sem' && <input type="checkbox" checked={sel.has(p.conta_id)} onChange={() => toggle(p.conta_id)} />}</td>
                  <td className="text-sm">{p.fornecedor || '—'}</td>
                  <td className="text-sm text-ink-600">{p.descricao}</td>
                  <td className="text-right font-semibold">{formatMoney(p.valor)}</td>
                  <td className="text-sm">{p.vencimento?.split('-').reverse().join('/')}</td>
                  <td className="text-xs text-ink-500">{p.tipo_documento || '—'}</td>
                  <td><span className={`text-[10px] px-2 py-0.5 rounded uppercase font-medium ${CHIP[p.forma_sugerida] || CHIP.sem}`}>{p.forma_sugerida === 'sem' ? 'sem forma' : p.forma_sugerida}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
